import DialogLayout from "./DialogLayout";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { inviteMembers } from "../api/ProjectApi";
import { SelectField } from "../components/ui";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { PipelineIcon } from "../PipelinePicker";
import LayoutAction from "../actions/LayoutAction";

type PropsType = {
  projectId: string;
  scope: string;
  visible: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  submitBtnLabel?: string;
};

const MemberInvitationDialog = (props: PropsType) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [emails, setEmails] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [selectedProjectId, setSelectedProjectId] = React.useState(props.projectId);

  React.useEffect(() => {
    setSelectedProjectId(props.projectId);
  }, [props.projectId]);

  const projects = useSelector((state: any) => state.projectReducer.get("projects"));
  const projectOptions = React.useMemo(() => {
    if (!projects || typeof projects.forEach !== "function") return [];
    const options: any[] = [];
    projects.forEach((item: any) => {
      options.push({
        value: item.get("id"),
        label: item.get("name") || t("Blank pipeline"),
        rawItem: item,
      });
    });
    return options;
  }, [projects, t]);
  const {
    visible,
    onSubmit = () => {},
    onClose,
    scope,
    submitBtnLabel = t("Send invitation"),
  } = props;

  const handleSubmit = () => {
    if (!emails) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Invitation"),
          type: "error",
          message: t("Please enter email addresses"),
        })
      );
      return;
    }

    inviteMembers(selectedProjectId, {
      emails: emails.split(","),
      message,
      scope,
    })
      .then(() => {
        onSubmit();
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("Invitation"),
            type: "error",
            message: t("Invitation sending failed!"),
          })
        );
      });
  };

  return (
    <DialogLayout show={visible}>
      <div
        className="flex flex-col w-full bg-white overflow-hidden"
        style={{ width: 480, maxWidth: "100%" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t("Invite team members")}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors focus:outline-none"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-500 leading-relaxed">
            {t("Enter the email address of the team members you want to invite.")}
          </p>

          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("Email address")}
              </label>
              <input
                type="email"
                placeholder="e.g. name@company.com"
                onChange={(e) => setEmails(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-shadow shadow-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("Pipeline")}
              </label>
              <SelectField
                className="w-full shadow-sm"
                value={
                  projectOptions.find((option: any) => option.value === selectedProjectId) || null
                }
                onChange={(selectedOption: any) => setSelectedProjectId(selectedOption?.value)}
                options={projectOptions}
                formatOptionLabel={(option: any) => (
                  <div className="flex items-center gap-2">
                    <PipelineIcon item={option.rawItem} size="sm" />
                    <span className="truncate">{option.label}</span>
                  </div>
                )}
                placeholder={t("Select a pipeline")}
                dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("Message")} <span className="text-gray-400 font-normal">({t("optional")})</span>
              </label>
              <textarea
                rows={3}
                placeholder={t("Add a personal note to the invitation...")}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-shadow shadow-sm"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            type="button"
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleSubmit}
            type="button"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
          >
            {submitBtnLabel}
          </button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default MemberInvitationDialog;
